import React, { useState, useMemo, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { ChevronIcon } from "../icons";
import { fmtDate, fmtDateFull } from "../utils/helpers";
import ClockInPage from "./ClockInPage";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

// Only these employee IDs may correct timesheet entries (CEO + Jonathan).
const EDITOR_IDS = ["01", "02"];

// Tokens — match storefront / styles.js
const ink = "#0A0A0F";
const paper = "#F4F2EC";
const white = "#FFFFFF";
const accent = "#0033FF";
const muted = "rgba(10,10,15,0.58)";
const success = "#22c55e";
const warn = "#b45309"; // darker amber for legibility on paper
const danger = "#ef4444";
const fontBody = "'Archivo', system-ui, sans-serif";
const fontMono = "'JetBrains Mono', monospace";

function dayLabel(d) {
  const dt = new Date(d + "T00:00:00");
  const wd = dt.toLocaleDateString("en-US", { weekday: "short" });
  return `${wd} · ${fmtDate(d)}`;
}

function getWeekKey(date) {
  const d = new Date(date + "T00:00:00");
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  return monday.toISOString().slice(0, 10);
}

function getWeekLabel(weekKey) {
  const mon = new Date(weekKey + "T00:00:00");
  const sun = new Date(mon);
  sun.setDate(sun.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${fmt(mon)} – ${fmt(sun)}`;
}

function getWeekEndLabel(weekKey) {
  const mon = new Date(weekKey + "T00:00:00");
  const sun = new Date(mon);
  sun.setDate(sun.getDate() + 6);
  return sun.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function getMonthLabel(mk) {
  const [y, m] = mk.split("-");
  const names = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return `${names[parseInt(m)]} ${y}`;
}

const fmtHrs = (h) => (h || 0).toFixed(2);
const fmtCost = (v) => `$${(v || 0).toFixed(2)}`;

// ─── Summary Bar ──────────────────────────────────────
function SummaryBar({ entries, rate, label }) {
  const totalHours = entries.reduce((s, e) => s + (e.hoursWorked || 0), 0);
  const workHours = entries.reduce((s, e) => s + (e.hoursWorking || 0), 0);
  const poHours = entries.reduce((s, e) => s + (e.hoursPostOffice || 0), 0);

  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", flexWrap: "wrap", gap: 8 }}>
      {label && (
        <span style={{
          fontFamily: fontMono, fontSize: 12, fontWeight: 700,
          color: ink, minWidth: 120, textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}>
          {label}
        </span>
      )}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <Stat label="Total Hrs" value={fmtHrs(totalHours)} color={ink} />
        <Stat label="Work Hrs" value={fmtHrs(workHours)} color={success} />
        <Stat label="PO Hrs" value={fmtHrs(poHours)} color={warn} />
        <Stat label="Work $" value={fmtCost(workHours * rate)} color={success} />
        <Stat label="PO $" value={fmtCost(poHours * rate)} color={warn} />
        <Stat label="Total $" value={fmtCost(totalHours * rate)} color={accent} />
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{
        fontFamily: fontMono, fontSize: 10, color: muted,
        textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700,
      }}>
        {label}
      </div>
      <div style={{
        fontFamily: fontMono, fontSize: 14, fontWeight: 700, color,
        marginTop: 2,
      }}>
        {value}
      </div>
    </div>
  );
}

// ─── Small per-day time field ─────────────────────────
function TimeField({ label, value }) {
  return (
    <div style={{ textAlign: "center", minWidth: 52 }}>
      <div style={{ fontFamily: fontMono, fontSize: 9, color: muted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>{label}</div>
      <div style={{ fontFamily: fontMono, fontSize: 13, fontWeight: 700, color: value ? ink : muted, marginTop: 2 }}>{value || "—"}</div>
    </div>
  );
}

// ─── Week row: summary header that expands into days ──
function WeekRow({ weekKey, weekLabel, entries, rate, canEdit, onEdit, empId, paidRecord, onTogglePaid }) {
  const [open, setOpen] = useState(false);
  const days = useMemo(() => [...entries].sort((a, b) => a.date.localeCompare(b.date)), [entries]);

  const weekHours = entries.reduce((s, e) => s + (e.hoursWorked || 0), 0);
  const weekCost = weekHours * rate;
  const paid = !!paidRecord;

  return (
    <div style={{ background: white, border: `2px solid ${paid ? success : ink}`, overflow: "hidden" }}>
      <div onClick={() => setOpen((o) => !o)} style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
        <span style={{ paddingLeft: 12, display: "flex", alignItems: "center" }}><ChevronIcon open={open} /></span>
        <div style={{ flex: 1, minWidth: 0 }}><SummaryBar entries={entries} rate={rate} label={weekLabel} /></div>
        <div onClick={(ev) => ev.stopPropagation()} style={{ paddingRight: 12, display: "flex", alignItems: "center", gap: 8 }}>
          {paid ? (
            <>
              <span title={`Paid ${fmtCost(paidRecord.amount)}${paidRecord.paidBy ? ` by ${paidRecord.paidBy}` : ""}${paidRecord.paidAt ? ` · ${paidRecord.paidAt}` : ""}`} style={{
                display: "inline-flex", alignItems: "center", gap: 5, fontFamily: fontMono, fontSize: 10, fontWeight: 700,
                padding: "4px 8px", background: success, color: white, textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap",
              }}>
                ✓ Paid · {fmtCost(paidRecord.amount)}
              </span>
              {canEdit && (
                <button onClick={() => onTogglePaid(empId, weekKey, false, weekCost, weekHours, paidRecord.id)} style={{
                  background: "none", border: `1.5px solid ${muted}`, color: muted, padding: "3px 8px",
                  fontFamily: fontMono, fontSize: 10, fontWeight: 700, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.04em",
                }}>Undo</button>
              )}
            </>
          ) : (
            canEdit && (
              <button onClick={() => onTogglePaid(empId, weekKey, true, weekCost, weekHours, null)} style={{
                background: ink, border: `2px solid ${ink}`, color: white, padding: "5px 12px",
                fontFamily: fontMono, fontSize: 11, fontWeight: 700, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap",
              }}>Mark Paid</button>
            )
          )}
        </div>
      </div>

      {open && days.map((e) => (
        <div key={e.id} style={{ borderTop: `1px solid ${ink}`, padding: "10px 16px", background: white }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <div style={{ minWidth: 110 }}>
              <div style={{ fontFamily: fontBody, fontSize: 13, fontWeight: 700, color: ink, textTransform: "uppercase", letterSpacing: "0.02em" }}>{dayLabel(e.date)}</div>
              {e.account && e.account !== "nycmfg" && (
                <span style={{ display: "inline-block", marginTop: 3, fontFamily: fontMono, fontSize: 9, fontWeight: 700, padding: "1px 6px", background: accent, color: white, textTransform: "uppercase", letterSpacing: "0.04em" }}>{e.account}</span>
              )}
            </div>
            <TimeField label="In" value={e.clockIn} />
            <TimeField label="Out" value={e.clockOut} />
            <TimeField label="PO" value={e.postOfficeStart} />
            {!e.clockOut && (
              <span style={{ fontFamily: fontMono, fontSize: 10, fontWeight: 700, color: warn, border: `1.5px solid ${warn}`, padding: "2px 6px", textTransform: "uppercase", letterSpacing: "0.04em" }}>No clock-out</span>
            )}
            <div style={{ flex: 1 }} />
            <Stat label="Work" value={fmtHrs(e.hoursWorking)} color={success} />
            <Stat label="PO" value={fmtHrs(e.hoursPostOffice)} color={warn} />
            <Stat label="Total" value={fmtHrs(e.hoursWorked)} color={ink} />
            <Stat label="$" value={fmtCost((e.hoursWorked || 0) * rate)} color={accent} />
            {canEdit && (
              <button onClick={() => onEdit(e)} style={{ ...S.iconBtn, padding: "6px 12px", fontSize: 12, fontWeight: 700 }}>Edit</button>
            )}
          </div>
          {e.editedBy && (
            <div style={{ fontFamily: fontMono, fontSize: 9, color: muted, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 6 }}>
              Edited by {e.editedBy}{e.editedAt ? ` · ${e.editedAt}` : ""}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Edit a day's punch times ─────────────────────────
function EditEntryModal({ entry, employeeName, editorName, onClose, onSaved }) {
  const [clockIn, setClockIn] = useState(entry.clockIn || "");
  const [clockOut, setClockOut] = useState(entry.clockOut || "");
  const [poStart, setPoStart] = useState(entry.postOfficeStart || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = () => {
    setSaving(true);
    setError("");
    fetch(`${API_URL}/api/timeclock/entries/${entry.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ clockIn, clockOut, postOfficeStart: poStart, editedBy: editorName }),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) { setError((d && d.error) || "Save failed"); setSaving(false); return; }
        onSaved();
        onClose();
      })
      .catch(() => { setError("Network error — try again"); setSaving(false); });
  };

  return ReactDOM.createPortal(
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={onClose} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)" }} />
      <div style={{ position: "relative", width: "100%", maxWidth: 420, background: paper, border: `2px solid ${ink}`, padding: 24, zIndex: 301 }}>
        <h3 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 20, fontWeight: 900, color: ink, textTransform: "uppercase", letterSpacing: "-0.01em", lineHeight: 1 }}>Edit Day</h3>
        <p style={{ fontFamily: fontMono, fontSize: 12, color: muted, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 6, marginBottom: 18 }}>
          {employeeName} · {fmtDateFull(entry.date)}
        </p>

        {!entry.clockOut && (
          <div style={{ background: white, border: `2px solid ${warn}`, color: warn, padding: "8px 12px", marginBottom: 16, fontFamily: fontMono, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            This day has no clock-out — likely a missed punch.
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={S.label}>Clock In</label>
          <input type="time" value={clockIn} onChange={(e) => setClockIn(e.target.value)} style={S.input} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={S.label}>Clock Out</label>
          <input type="time" value={clockOut} onChange={(e) => setClockOut(e.target.value)} style={S.input} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={S.label}>Post Office Start</label>
          <input type="time" value={poStart} onChange={(e) => setPoStart(e.target.value)} style={S.input} />
        </div>
        <p style={{ fontFamily: fontMono, fontSize: 10, color: muted, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 16 }}>
          Hours recalculate automatically. Leave Post Office blank if not used.
        </p>

        {error && (
          <div style={{ color: danger, fontFamily: fontMono, fontSize: 12, fontWeight: 700, marginBottom: 14 }}>{error}</div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={S.clearBtn}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ ...S.saveJobBtn, width: "auto", padding: "10px 24px", opacity: saving ? 0.6 : 1, cursor: saving ? "default" : "pointer" }}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Employee Time Card ───────────────────────────────
function EmployeeTimeCard({ employee, entries, onRefresh, canEdit, editorName, account, paidLookup, onTogglePaid }) {
  const [expanded, setExpanded] = useState(false);
  const [view, setView] = useState("weekly");
  const [editingEntry, setEditingEntry] = useState(null);

  const totalHours = entries.reduce((s, e) => s + (e.hoursWorked || 0), 0);
  const workHours = entries.reduce((s, e) => s + (e.hoursWorking || 0), 0);
  const poHours = entries.reduce((s, e) => s + (e.hoursPostOffice || 0), 0);
  const rate = employee.rate;

  const weeklyData = useMemo(() => {
    const map = {};
    entries.forEach((e) => { const wk = getWeekKey(e.date); if (!map[wk]) map[wk] = []; map[wk].push(e); });
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [entries]);

  const monthlyData = useMemo(() => {
    const map = {};
    entries.forEach((e) => { const mk = e.date.slice(0, 7); if (!map[mk]) map[mk] = []; map[mk].push(e); });
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [entries]);

  const quarterlyData = useMemo(() => {
    const map = {};
    entries.forEach((e) => {
      const m = parseInt(e.date.slice(5, 7));
      const y = e.date.slice(0, 4);
      const q = `${y}-Q${Math.ceil(m / 3)}`;
      if (!map[q]) map[q] = [];
      map[q].push(e);
    });
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [entries]);

  const uniqueWeeks = new Set(entries.map((e) => getWeekKey(e.date))).size;

  // Latest week that's been marked paid (under the current account context) → "paid through" indicator.
  const paidThrough = useMemo(() => {
    const prefix = `${employee.empId}|`;
    const suffix = `|${account}`;
    const weeks = Object.keys(paidLookup)
      .filter((k) => k.startsWith(prefix) && k.endsWith(suffix))
      .map((k) => k.split("|")[1]);
    if (!weeks.length) return null;
    return weeks.sort().reverse()[0];
  }, [paidLookup, employee.empId, account]);

  return (
    <div style={{ marginBottom: 12 }}>
      <button onClick={() => setExpanded(!expanded)} style={{ ...S.monthHeader }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <ChevronIcon open={expanded} />
          <div>
            <span style={{
              fontFamily: fontBody, fontSize: 15, fontWeight: 700,
              color: ink, textTransform: "uppercase", letterSpacing: "0.02em",
            }}>
              {employee.name}
            </span>
            <span style={{
              fontFamily: fontMono, fontSize: 11, color: muted,
              marginLeft: 10, textTransform: "uppercase", letterSpacing: "0.06em",
            }}>
              {employee.position}
            </span>
          </div>
          <span style={S.jobCountBadge}>{entries.length} day{entries.length !== 1 ? "s" : ""}</span>
          {paidThrough && (
            <span style={{
              fontFamily: fontMono, fontSize: 10, fontWeight: 700, padding: "3px 8px",
              background: success, color: white, textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap",
            }}>
              ✓ Paid thru {getWeekEndLabel(paidThrough)}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontFamily: fontMono, fontSize: 10, color: muted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>Work Hrs</div>
            <div style={{ fontFamily: fontMono, fontSize: 14, fontWeight: 700, color: success }}>{fmtHrs(workHours)}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontFamily: fontMono, fontSize: 10, color: muted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>PO Hrs</div>
            <div style={{ fontFamily: fontMono, fontSize: 14, fontWeight: 700, color: warn }}>{fmtHrs(poHours)}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontFamily: fontMono, fontSize: 10, color: muted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>Total Cost</div>
            <div style={{ fontFamily: fontMono, fontSize: 14, fontWeight: 700, color: accent }}>{fmtCost(totalHours * rate)}</div>
          </div>
        </div>
      </button>

      {expanded && (
        <div style={{
          background: paper, border: `2px solid ${ink}`, borderTop: "none",
          padding: "16px 16px 12px",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
            <nav style={S.nav}>
              {["weekly", "monthly", "quarterly", "annual"].map((v) => (
                <button key={v} onClick={() => setView(v)} style={{ ...S.navBtn, fontSize: 12, padding: "8px 14px", ...(view === v ? S.navBtnActive : {}) }}>
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </button>
              ))}
            </nav>
            <button onClick={onRefresh} style={{ ...S.iconBtn, padding: "6px 12px" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" /></svg>
            </button>
          </div>

          {/* Weekly View — each week expands into days */}
          {view === "weekly" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {weeklyData.map(([wk, wEntries]) => (
                <WeekRow
                  key={wk}
                  weekKey={wk}
                  weekLabel={getWeekLabel(wk)}
                  entries={wEntries}
                  rate={rate}
                  canEdit={canEdit}
                  onEdit={setEditingEntry}
                  empId={employee.empId}
                  paidRecord={paidLookup[`${employee.empId}|${wk}|${account}`] || null}
                  onTogglePaid={onTogglePaid}
                />
              ))}
              {weeklyData.length === 0 && <div style={{ textAlign: "center", color: muted, padding: 20, fontFamily: fontMono, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>No entries.</div>}
            </div>
          )}

          {/* Monthly View */}
          {view === "monthly" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {monthlyData.map(([mk, mEntries]) => (
                <div key={mk} style={{ background: white, border: `2px solid ${ink}`, overflow: "hidden" }}>
                  <SummaryBar entries={mEntries} rate={rate} label={getMonthLabel(mk)} />
                </div>
              ))}
              {monthlyData.length === 0 && <div style={{ textAlign: "center", color: muted, padding: 20, fontFamily: fontMono, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>No entries.</div>}
            </div>
          )}

          {/* Quarterly View */}
          {view === "quarterly" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {quarterlyData.map(([qk, qEntries]) => (
                <div key={qk} style={{ background: white, border: `2px solid ${ink}`, overflow: "hidden" }}>
                  <SummaryBar entries={qEntries} rate={rate} label={qk.replace("-", " ")} />
                </div>
              ))}
              {quarterlyData.length === 0 && <div style={{ textAlign: "center", color: muted, padding: 20, fontFamily: fontMono, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>No entries.</div>}
            </div>
          )}

          {/* Annual View */}
          {view === "annual" && (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12, marginBottom: 16 }}>
                <div style={S.statCard}><span style={S.statLabel}>Total Hours</span><span style={{ ...S.statValue, fontSize: 22 }}>{fmtHrs(totalHours)}</span></div>
                <div style={S.statCard}><span style={S.statLabel}>Work Hours</span><span style={{ ...S.statValue, fontSize: 22, color: success }}>{fmtHrs(workHours)}</span></div>
                <div style={S.statCard}><span style={S.statLabel}>PO Hours</span><span style={{ ...S.statValue, fontSize: 22, color: warn }}>{fmtHrs(poHours)}</span></div>
                <div style={S.statCard}><span style={S.statLabel}>Work $</span><span style={{ ...S.statValue, fontSize: 22, color: success }}>{fmtCost(workHours * rate)}</span></div>
                <div style={S.statCard}><span style={S.statLabel}>PO $</span><span style={{ ...S.statValue, fontSize: 22, color: warn }}>{fmtCost(poHours * rate)}</span></div>
                <div style={S.statCard}><span style={S.statLabel}>Total Cost</span><span style={{ ...S.statValue, fontSize: 22, color: accent }}>{fmtCost(totalHours * rate)}</span></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12 }}>
                <div style={S.statCard}><span style={S.statLabel}>Days Worked</span><span style={{ ...S.statValue, fontSize: 22 }}>{entries.length}</span></div>
                <div style={S.statCard}><span style={S.statLabel}>Weeks Active</span><span style={{ ...S.statValue, fontSize: 22 }}>{uniqueWeeks}</span></div>
                <div style={S.statCard}><span style={S.statLabel}>Avg Hrs/Day</span><span style={{ ...S.statValue, fontSize: 22 }}>{entries.length > 0 ? fmtHrs(totalHours / entries.length) : "0"}</span></div>
                <div style={S.statCard}><span style={S.statLabel}>Avg Hrs/Week</span><span style={{ ...S.statValue, fontSize: 22 }}>{uniqueWeeks > 0 ? fmtHrs(totalHours / uniqueWeeks) : "0"}</span></div>
              </div>
            </div>
          )}
        </div>
      )}

      {editingEntry && (
        <EditEntryModal
          entry={editingEntry}
          employeeName={employee.name}
          editorName={editorName}
          onClose={() => setEditingEntry(null)}
          onSaved={onRefresh}
        />
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────
export default function TimeClockPage({ employees, currentEmpId }) {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showClockIn, setShowClockIn] = useState(false);
  const [dbEntries, setDbEntries] = useState([]);
  const [paidWeeks, setPaidWeeks] = useState([]);
  const [accounts, setAccounts] = useState([{ key: "nycmfg", label: "NYC MFG" }]);
  const [accountFilter, setAccountFilter] = useState("all"); // "all" | account key

  const fetchEntries = useCallback(() => {
    fetch(`${API_URL}/api/timeclock/entries?year=${selectedYear}`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setDbEntries(data); })
      .catch(() => {});
  }, [selectedYear]);

  // Paid records are tiny (one per employee/week) so we pull them all — avoids year-boundary week misses.
  const fetchPaid = useCallback(() => {
    fetch(`${API_URL}/api/salary-payments`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setPaidWeeks(data); })
      .catch(() => {});
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => { fetchPaid(); }, [fetchPaid]);
  useEffect(() => {
    fetch(`${API_URL}/api/clock-accounts`).then((r) => r.json()).then((d) => { if (Array.isArray(d) && d.length) setAccounts(d); }).catch(() => {});
  }, []);

  const canEdit = EDITOR_IDS.includes(String(currentEmpId));
  const editorName = (employees.find((e) => String(e.empId) === String(currentEmpId)) || {}).name || String(currentEmpId || "");

  const paidLookup = useMemo(() => {
    const m = {};
    paidWeeks.forEach((p) => { m[`${p.employeeId}|${p.weekKey}|${p.account}`] = p; });
    return m;
  }, [paidWeeks]);

  const togglePaid = useCallback((empId, weekKey, makePaid, amount, hours, existingId) => {
    if (makePaid) {
      fetch(`${API_URL}/api/salary-payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ employeeId: empId, weekKey, account: accountFilter, amount, hours, paidBy: editorName }),
      }).then(() => fetchPaid()).catch(() => {});
    } else if (existingId) {
      fetch(`${API_URL}/api/salary-payments/${existingId}`, { method: "DELETE" })
        .then(() => fetchPaid()).catch(() => {});
    }
  }, [accountFilter, editorName, fetchPaid]);

  // Filter the whole page by billing account so e.g. Darkfin Labs hours can be totalled for invoicing.
  const yearEntries = accountFilter === "all" ? dbEntries : dbEntries.filter((e) => (e.account || "nycmfg") === accountFilter);
  const accountLabel = (accounts.find((a) => a.key === accountFilter) || {}).label || accountFilter;
  // Only surface the filter once a non-default account actually has hours (keeps the UI clean until Darkfin is in use).
  const usedAccounts = new Set(dbEntries.map((e) => e.account || "nycmfg"));
  const showAccountFilter = accounts.length > 1 && (usedAccounts.size > 1 || accountFilter !== "all");
  const getRate = (empId) => { const emp = employees.find((e) => String(e.empId) === String(empId)); return emp ? emp.rate : 0; };
  const totalHours = yearEntries.reduce((s, e) => s + (e.hoursWorked || 0), 0);
  const totalWorking = yearEntries.reduce((s, e) => s + (e.hoursWorking || 0), 0);
  const totalPO = yearEntries.reduce((s, e) => s + (e.hoursPostOffice || 0), 0);
  const totalWorkCost = yearEntries.reduce((s, e) => s + (e.hoursWorking || 0) * getRate(e.employeeId), 0);
  const totalPOCost = yearEntries.reduce((s, e) => s + (e.hoursPostOffice || 0) * getRate(e.employeeId), 0);
  const totalCost = yearEntries.reduce((s, e) => s + (e.hoursWorked || 0) * getRate(e.employeeId), 0);

  if (showClockIn) {
    return (
      <div>
        <button onClick={() => { setShowClockIn(false); fetchEntries(); }} style={{
          display: "flex", alignItems: "center", gap: 6, background: "none", border: "none",
          color: ink, fontSize: 13, fontWeight: 700, cursor: "pointer",
          fontFamily: fontBody, textTransform: "uppercase", letterSpacing: "0.06em",
          marginBottom: 12, padding: 0,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
          Back to Time Clock
        </button>
        <ClockInPage />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{
            fontFamily: "'Archivo Black', 'Archivo', sans-serif",
            fontSize: 32, fontWeight: 900, color: ink, marginBottom: 4,
            letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1,
          }}>
            Time Clock
          </h2>
          <p style={{
            fontFamily: fontMono, fontSize: 12, color: muted,
            textTransform: "uppercase", letterSpacing: "0.06em",
          }}>
            Track employee hours · weekly · monthly · quarterly · annually
          </p>
        </div>
        <button onClick={() => setShowClockIn(true)} style={S.addBtn}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
          View Time Clock
        </button>
      </div>

      {/* Year selector */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => setSelectedYear((y) => y - 1)} style={{ ...S.iconBtn, padding: "6px 10px", fontSize: 16 }}>‹</button>
          <span style={{
            fontFamily: fontMono, fontSize: 22, fontWeight: 700,
            color: ink, minWidth: 60, textAlign: "center",
            letterSpacing: "-0.02em",
          }}>
            {selectedYear}
          </span>
          <button onClick={() => setSelectedYear((y) => y + 1)} style={{ ...S.iconBtn, padding: "6px 10px", fontSize: 16 }}>›</button>
        </div>
        <span style={{
          fontFamily: fontMono, color: muted, fontSize: 12,
          textTransform: "uppercase", letterSpacing: "0.06em",
        }}>
          {fmtHrs(totalHours)} total hours in {selectedYear}{accountFilter !== "all" ? ` · ${accountLabel}` : ""}
        </span>
      </div>

      {/* Account filter — total/bill hours per billing account */}
      {showAccountFilter && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          <span style={{ fontFamily: fontMono, fontSize: 11, fontWeight: 700, color: muted, textTransform: "uppercase", letterSpacing: "0.08em", marginRight: 4 }}>Account</span>
          {[{ key: "all", label: "All" }, ...accounts].map((a) => {
            const active = a.key === accountFilter;
            return (
              <button key={a.key} onClick={() => setAccountFilter(a.key)} style={{
                padding: "6px 14px", borderRadius: 0, border: `2px solid ${ink}`,
                background: active ? ink : "transparent", color: active ? white : ink,
                fontFamily: fontMono, fontSize: 12, fontWeight: 700, cursor: "pointer",
                textTransform: "uppercase", letterSpacing: "0.06em",
              }}>{a.label}</button>
            );
          })}
        </div>
      )}

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 24 }}>
        <div style={S.statCard}><span style={S.statLabel}>Total Hours</span><span style={S.statValue}>{fmtHrs(totalHours)}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Work Hours</span><span style={{ ...S.statValue, color: success }}>{fmtHrs(totalWorking)}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>PO Hours</span><span style={{ ...S.statValue, color: warn }}>{fmtHrs(totalPO)}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Work $</span><span style={{ ...S.statValue, color: success }}>{fmtCost(totalWorkCost)}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>PO $</span><span style={{ ...S.statValue, color: warn }}>{fmtCost(totalPOCost)}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Total Cost</span><span style={{ ...S.statValue, color: accent }}>{fmtCost(totalCost)}</span></div>
      </div>

      {/* Employee cards */}
      {employees.map((emp) => {
        const empEntries = yearEntries.filter((e) => String(e.employeeId) === String(emp.empId));
        // When billing a specific account, hide employees with no hours on it.
        if (accountFilter !== "all" && empEntries.length === 0) return null;
        return (
          <EmployeeTimeCard key={emp.id} employee={emp} entries={empEntries} onRefresh={fetchEntries} canEdit={canEdit} editorName={editorName} account={accountFilter} paidLookup={paidLookup} onTogglePaid={togglePaid} />
        );
      })}

      {employees.length === 0 && (
        <div style={{
          background: white, border: `2px solid ${ink}`,
          padding: 40, textAlign: "center", color: muted,
          fontFamily: fontMono, fontSize: 13, textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}>
          Add employees first in the Employees section.
        </div>
      )}
    </div>
  );
}
